import React, {useState, useEffect} from "react"
import {useAccessToken} from "../../services/auth"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {
	getEmailTemplatesList,
	EmailTemplate,
	publishEmailTemplateVersion,
	createEmailTemplateVersion,
} from "../../resources/emailTemplates"
import {
	DataTable,
	DataTableBody,
	DataTableCard,
	DataTableCell,
	DataTableHead,
	DataTableRow,
	TablePending,
} from "../../components/DataTable/DataTable"
import {DataTableActionButton, DataTableActionItem} from "../../components/DataTable/DataTableActionButton"
import Tag from "../../components/Tag/Tag"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import Button from "../../components/Button/Button"
import TitleBar from "../../components/TitleBar/TitleBar"
import MustacheEditor from "../../components/MustacheEditor/MustacheEditor"

interface EmailTemplateVersionsProps {
	templateId: string
	templateName?: string
	refresh: () => void
}

export default function EmailTemplateVersions({templateId, templateName, refresh}: EmailTemplateVersionsProps) {
	const accessToken = useAccessToken()
	const [displayName] = useState<string>(templateName || templateId)
	const [editTemplateVersion, setEditTemplateVersion] = useState<EmailTemplate | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [forcePublish, setForcePublish] = useState<boolean>(false)

	const templatesResult = useAsyncResult(
		() =>
			getEmailTemplatesList(accessToken, {
				includeUnpublished: true,
				filterByTemplateId: templateId,
			}),
		[accessToken, templateId, refresh]
	)

	const [publishStatus, publishTemplate, resetPublishStatus] = useAsyncResultIdle((template: EmailTemplate) =>
		publishEmailTemplateVersion(accessToken, {
			templateId: template.attributes.templateId,
			version: template.attributes.version,
		})
	)

	// Fix for infinite loop: Track previous publish status and only refresh when status changes to success
	const publishStatusRef = React.useRef(publishStatus)

	useEffect(() => {
		// Only refresh if we have a new successful result and it's different from the previous one
		const prevStatus = publishStatusRef.current
		const isPrevSuccess = prevStatus && "value" in prevStatus
		const isCurrentSuccess = publishStatus && "value" in publishStatus

		if (isCurrentSuccess && (!isPrevSuccess || prevStatus !== publishStatus)) {
			refresh()
			// Reset status after handling to prevent re-triggering
			resetPublishStatus()
		}

		// Update the ref with current status
		publishStatusRef.current = publishStatus
	}, [publishStatus, refresh, resetPublishStatus])

	// Simple formatting function for dates
	const formatDate = (date: Date): string => {
		return date.toLocaleDateString(undefined, {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		})
	}

	const handleEditTemplate = (template: EmailTemplate) => {
		setEditTemplateVersion(template)
	}

	const cancelEditing = () => {
		setEditTemplateVersion(null)
		setError(null)
		setForcePublish(false)
	}

	const handleSaveTemplate = async (htmlContent: string, subject?: string, samples?: string) => {
		if (!editTemplateVersion) return

		if (!subject) {
			setError("Email subject is required")
			return
		}

		setError(null)

		try {
			await createEmailTemplateVersion(accessToken, {
				subject: subject,
				htmlContent,
				templateId: editTemplateVersion.attributes.templateId,
				forcePublish: forcePublish,
				samples: samples || "[]",
			})

			refresh()
			cancelEditing()
		} catch (err) {
			setError("Failed to update template version")
			console.error(err)
		}
	}

	return (
		<div className={"email-template-versions columns"}>
			<DataTableCard className={"email-template-versions-card column is-12"}>
				<TitleBar title={displayName}>
					{editTemplateVersion && (
						<Button theme="neutral" onClick={cancelEditing} className="ml-4">
							Cancel
						</Button>
					)}
				</TitleBar>

				{editTemplateVersion ? (
					<div className="email-template-editor-container">
						<div className="email-template-editor-header">
							<h2>Editing Template Version: {editTemplateVersion.attributes.subject}</h2>
							<p>Template ID: {editTemplateVersion.attributes.templateId}</p>
							<p>Version: {editTemplateVersion.attributes.version}</p>
							<div className="publish-option">
								<label>
									<input type="checkbox" checked={forcePublish} onChange={(e) => setForcePublish(e.target.checked)} />
									<span> Publish this version immediately</span>
								</label>
							</div>
							{error && <div className="error-message">{error}</div>}
						</div>

						<MustacheEditor
							initialHtml={editTemplateVersion.attributes.htmlContent}
							onSave={handleSaveTemplate}
							initialSubject={editTemplateVersion.attributes.subject}
							showSubjectField={true}
							samples={editTemplateVersion.attributes.samples}
						/>
					</div>
				) : (
					templatesResult.match(
						() => <TablePending />,
						(templatesResult) => {
							const templates = Array.isArray(templatesResult.data) ? templatesResult.data : []
							// Sort templates by version in descending order
							const sortedTemplates = [...templates].sort((a, b) => b.attributes.version - a.attributes.version)

							return (
								<DataTable>
									<DataTableHead>
										<DataTableRow>
											<th>ID</th>
											<th>Subject</th>
											<th>Version</th>
											<th>Published</th>
											<th>Created At</th>
											<th></th>
										</DataTableRow>
									</DataTableHead>
									<DataTableBody>
										{sortedTemplates.map((template: EmailTemplate) => (
											<DataTableRow key={template.id} style={{cursor: "default"}}>
												<DataTableCell>{template.id}</DataTableCell>
												<DataTableCell>{template.attributes.subject}</DataTableCell>
												<DataTableCell>{template.attributes.version}</DataTableCell>
												<DataTableCell>
													{template.attributes.isPublished ? (
														<Tag size={"small"} content={"Published"} variant={"outline"} theme={"success"} />
													) : (
														<Tag size={"small"} content={"Draft"} variant={"outline"} theme={"dark"} />
													)}
												</DataTableCell>
												<DataTableCell>{formatDate(new Date(template.attributes.createdAt))}</DataTableCell>
												<DataTableActionButton enableActions={true}>
													<DataTableActionItem
														onClick={() => handleEditTemplate(template)}
														title={"Edit"}
														icon={"pencil-1--interface-essential"}
													/>
													{!template.attributes.isPublished && (
														<DataTableActionItem
															onClick={() => publishTemplate(template)}
															title={"Publish"}
															icon={"email-send-1--emails"}
														/>
													)}
												</DataTableActionButton>
											</DataTableRow>
										))}
									</DataTableBody>
								</DataTable>
							)
						},
						(error) => <div className="error-message">Error loading templates: {error.toString()}</div>
					)
				)}
			</DataTableCard>
		</div>
	)
}
